import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'

const API = ''

const isNumericOption = (val) => {
    if (typeof val !== 'string' && typeof val !== 'number') return false
    const s = String(val).trim()
    return /^\d*\.?\d+$/.test(s)
}

const getNumericStepInfo = (options) => {
    const numbers = options.map(o => parseFloat(o)).filter(n => !isNaN(n))
    if (numbers.length === 0) return null
    const min = Math.min(...numbers)
    const max = Math.max(...numbers)
    if (min === max) return null
    const range = max - min
    const stepSize = range > 1000 ? 50 : range > 100 ? 5 : range > 10 ? 1 : 0.05
    return { min, max, stepSize }
}

const ProductConfigurator = ({ products, categoryId, categoryName, onFilter }) => {
    const [selections, setSelections] = useState({})   // { [stepKey]: string[] or { min, max } }
    const [openStep, setOpenStep] = useState(null)
    const [lookupValues, setLookupValues] = useState([])
    const [productLookups, setProductLookups] = useState({})
    const [activeCategoryLookups, setActiveCategoryLookups] = useState([])
    const containerRef = useRef(null)

    useEffect(() => {
        if (categoryId) {
            axios.get(`${API}/api/category-lookup-tables/${categoryId}`)
                .then(r => setActiveCategoryLookups(Array.isArray(r.data) ? r.data : []))
                .catch(() => setActiveCategoryLookups([]))
        }
    }, [categoryId])

    useEffect(() => {
        axios.get(`${API}/api/lookup-values`)
            .then(r => setLookupValues(Array.isArray(r.data) ? r.data : []))
            .catch(() => setLookupValues([]))
    }, [])

    useEffect(() => {
        const loadAssociations = async () => {
            const map = {}
            await Promise.all(products.map(async p => {
                try {
                    const r = await axios.get(`${API}/api/products/${p.id}/lookup-values`)
                    map[p.id] = r.data.map(v => v.value_id)
                } catch { map[p.id] = [] }
            }))
            setProductLookups(map)
        }
        if (products.length > 0) loadAssociations()
    }, [products])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpenStep(null)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const lookupBySlug = useMemo(() => {
        const grouped = {}
        const vals = Array.isArray(lookupValues) ? lookupValues : []
        vals.forEach(v => {
            if (!grouped[v.slug]) grouped[v.slug] = []
            grouped[v.slug].push(v)
        })
        return grouped
    }, [lookupValues])

    const lineSizes = useMemo(() => [...new Set(products.map(p => p.lineSize).filter(Boolean))].sort(), [products])
    const pressures = useMemo(() => {
        const meop = [...new Set(products.map(p => p.MEOP).filter(Boolean))].sort()
        return meop.length > 0 ? meop : [...new Set(products.map(p => p.operatingPressures).filter(Boolean))].sort()
    }, [products])
    const serviceTypes = useMemo(() => {
        const types = new Set()
        products.forEach(p => {
            ;(p.lookup_values || []).forEach(v => {
                if (v.slug === 'Cryogenic' || v.slug === 'service_type' || v.slug === 'cryo') {
                    types.add(v.value)
                }
            })
            if (p.cryogenic && p.cryogenic !== 'No' && p.cryogenic !== 'False') {
                types.add('Cryogenic')
            }
        })
        return [...types]
    }, [products])
    const regulatorStyles = useMemo(() => {
        const styles = [...new Set(products.map(p => p.productType).filter(Boolean))]
        return styles.length > 1 ? styles : []
    }, [products])
    const cvValues = useMemo(() => [...new Set(products.map(p => p.flowCoefficient).filter(Boolean))].sort(), [products])
    const inletPressures = useMemo(() => [...new Set(products.map(p => p.inletPressure).filter(Boolean))].sort(), [products])

    const steps = useMemo(() => {
        let rawSteps = []
        if (activeCategoryLookups.length > 0) {
            rawSteps = activeCategoryLookups.map(lt => {
                const values = lookupValues.filter(v => v.table_id === lt.lookup_table_id || v.slug === lt.slug)
                const filteredValues = values.filter(val => products.some(p => (productLookups[p.id] || []).includes(val.id)))
                const sortedValues = [...filteredValues].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                return { key: lt.slug, label: lt.table_label || lt.label, options: sortedValues.map(v => v.value), lookup: true, slug: lt.slug }
            }).filter(step => step.options.length > 0)
        } else {
            const s = []
            const ltLineSizes = (lookupBySlug['line_size'] || []).map(v => v.value)
            if (ltLineSizes.length > 0) s.push({ key: 'lineSize', label: 'Line Size', options: ltLineSizes, lookup: true, slug: 'line_size' })
            else if (lineSizes.length > 0) s.push({ key: 'lineSize', label: 'Line Size', options: lineSizes })

            const ltMaxPressure = (lookupBySlug['meop'] || []).map(v => v.value)
            if (ltMaxPressure.length > 0) s.push({ key: 'pressure', label: 'MEOP', options: ltMaxPressure, lookup: true, slug: 'meop' })
            else if (pressures.length > 0) s.push({ key: 'pressure', label: 'MEOP', options: pressures })

            const serviceSlug = (lookupBySlug['Cryogenic'] || []).length > 0
                ? 'Cryogenic'
                : (lookupBySlug['service_type'] || []).length > 0
                    ? 'service_type'
                    : null
            const ltServiceType = serviceSlug ? (lookupBySlug[serviceSlug] || []).map(v => v.value) : []
            if (ltServiceType.length > 0) s.push({ key: 'serviceType', label: 'Service Type', options: [...new Set(ltServiceType)], lookup: true, slug: serviceSlug })
            else if (serviceTypes.length > 0) s.push({ key: 'serviceType', label: 'Service Type', options: serviceTypes })

            const ltApplication = (lookupBySlug['application'] || []).map(v => v.value)
            if (ltApplication.length > 0) s.push({ key: 'application', label: 'Application', options: ltApplication, lookup: true, slug: 'application' })

            if (regulatorStyles.length > 0) s.push({ key: 'regulatorStyle', label: 'Product Type', options: regulatorStyles })

            const ltCv = (lookupBySlug['flow_coefficient'] || []).map(v => v.value)
            if (ltCv.length > 0) s.push({ key: 'cv', label: 'Required Cv', options: ltCv, lookup: true, slug: 'flow_coefficient' })
            else if (cvValues.length > 0) s.push({ key: 'cv', label: 'Required Cv', options: cvValues })

            if (inletPressures.length > 0) s.push({ key: 'inletPressure', label: 'Max Inlet Pressure', options: inletPressures })
            
            rawSteps = s
        }

        // Post-process to automatically mark steps as numeric if all values are valid numbers
        return rawSteps.map(step => {
            const numInfo = step.options.length > 1 && step.options.every(isNumericOption) ? getNumericStepInfo(step.options) : null
            if (numInfo) {
                return { ...step, isNumeric: true, ...numInfo }
            }
            return step
        })
    }, [activeCategoryLookups, lookupValues, products, productLookups, lookupBySlug, lineSizes, pressures, serviceTypes, regulatorStyles, cvValues, inletPressures])

    const matchesField = useCallback((product, key, value, step) => {
        if (!value) return true

        // Handle numeric range matching
        if (step && step.isNumeric && typeof value === 'object') {
            if (step.lookup && step.slug) {
                // Find all values for this product associated with this slug
                const productValues = lookupValues
                    .filter(lv => lv.slug === step.slug && (productLookups[product.id] || []).includes(lv.id))
                    .map(lv => parseFloat(lv.value))
                    .filter(val => !isNaN(val))
                if (productValues.length === 0) return false
                return productValues.some(val => val >= value.min && val <= value.max)
            } else {
                // Direct column fallback
                const fieldVal = parseFloat(product[key] || product[step.slug])
                if (!isNaN(fieldVal)) {
                    return fieldVal >= value.min && fieldVal <= value.max
                }
                return false
            }
        }

        if (step && step.lookup && step.slug) {
            const productValueIds = productLookups[product.id] || []
            const matchingLookup = lookupValues.find(lv => lv.slug === step.slug && lv.value === value)
            return matchingLookup ? productValueIds.includes(matchingLookup.id) : false
        }
        switch (key) {
            case 'lineSize': return (product.lineSize || '') === value
            case 'pressure': return (product.MEOP || '') === value
            case 'serviceType': {
                const vals = (product.lookup_values || [])
                    .filter(v => v.slug === 'Cryogenic' || v.slug === 'service_type' || v.slug === 'cryo')
                    .map(v => String(v.value).toLowerCase())
                if (vals.length > 0) {
                    const needle = String(value).toLowerCase()
                    return vals.some(v => v === needle || v.includes(needle) || needle.includes(v))
                }
                if (value === 'Cryogenic' || String(value).toLowerCase().includes('cryo')) {
                    return product.cryogenic && product.cryogenic !== 'No' && product.cryogenic !== 'False'
                }
                if (value === 'Ambient' || String(value).toLowerCase().includes('ambient')) {
                    return false // no fabricated Ambient when product has no service-temp data
                }
                return true
            }
            case 'application': return true
            case 'regulatorStyle': return (product.productType || '') === value
            case 'cv': return (product.flowCoefficient || '') === value
            case 'inletPressure': return (product.inletPressure || '') === value
            default: return true
        }
    }, [productLookups, lookupValues])

    // OR within step, AND across steps
    const matchedProducts = useMemo(() => {
        const activeSteps = steps.filter(s => {
            const sel = selections[s.key]
            if (!sel) return false
            if (s.isNumeric && typeof sel === 'object') {
                return sel.min !== s.min || sel.max !== s.max
            }
            return sel.length > 0
        })
        if (activeSteps.length === 0) return []
        return products.filter(p =>
            activeSteps.every(step => {
                const sel = selections[step.key]
                if (step.isNumeric && sel && typeof sel === 'object') {
                    return matchesField(p, step.key, sel, step)
                }
                const vals = sel || []
                return vals.some(val => matchesField(p, step.key, val, step))
            })
        )
    }, [products, selections, steps, matchesField])

    // Options shown in a dropdown: only those that yield ≥1 result given other steps' selections
    const getAvailableOptions = useCallback((step) => {
        if (step.isNumeric) return []
        return step.options.filter(opt => {
            return products.some(p => {
                if (!matchesField(p, step.key, opt, step)) return false
                return steps.every(s => {
                    if (s.key === step.key) return true
                    const sel = selections[s.key]
                    if (!sel) return true
                    if (s.isNumeric && typeof sel === 'object') {
                        return matchesField(p, s.key, sel, s)
                    }
                    const vals = sel || []
                    if (vals.length === 0) return true
                    return vals.some(val => matchesField(p, s.key, val, s))
                })
            })
        })
    }, [products, selections, steps, matchesField])

    const toggleOption = (stepKey, value) => {
        setSelections(prev => {
            const current = prev[stepKey] || []
            const next = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value]
            return { ...prev, [stepKey]: next }
        })
    }

    const setNumericRange = (stepKey, minVal, maxVal) => {
        setSelections(prev => ({
            ...prev,
            [stepKey]: { min: minVal, max: maxVal }
        }))
    }

    const reset = () => {
        setSelections({})
        setOpenStep(null)
    }

    useEffect(() => {
        const activeSteps = steps.filter(s => {
            const sel = selections[s.key]
            if (!sel) return false
            if (s.isNumeric && typeof sel === 'object') {
                return sel.min !== s.min || sel.max !== s.max
            }
            return sel.length > 0
        })
        if (onFilter) {
            if (activeSteps.length === 0) onFilter(null)
            else onFilter(matchedProducts.map(p => p.id))
        }
    }, [matchedProducts, selections, onFilter, steps])

    const filledCount = steps.filter(s => {
        const sel = selections[s.key]
        if (!sel) return false
        if (s.isNumeric && typeof sel === 'object') {
            return sel.min !== s.min || sel.max !== s.max
        }
        return sel.length > 0
    }).length

    const totalSelected = Object.entries(selections).reduce((sum, [key, val]) => {
        if (!val) return sum
        const step = steps.find(s => s.key === key)
        if (step && step.isNumeric && typeof val === 'object') {
            return (val.min !== step.min || val.max !== step.max) ? sum + 1 : sum
        }
        return sum + (Array.isArray(val) ? val.length : 0)
    }, 0)

    if (steps.length === 0) {
        return (
            <div className="pfc-cfg">
                <div className="pfc-cfg-hdr">
                    <div className="pfc-cfg-hdr-title">Need Help Choosing?</div>
                    <div className="pfc-cfg-hdr-sub">Our engineers can match you to the right product</div>
                </div>
                <div className="pfc-cfg-contact">
                    <div className="pfc-cfg-contact-text">Tell us about your application and we'll recommend the best solution.</div>
                    <div className="pfc-cfg-contact-phone">(916) 626-3029</div>
                </div>
            </div>
        )
    }

    return (
        <div className="pfc-cfg" ref={containerRef}>
            <div className="pfc-cfg-hdr">
                <div>
                    <div className="pfc-cfg-hdr-title">Filter Products</div>
                    <div className="pfc-cfg-hdr-sub">Narrow results by specification</div>
                </div>
                {totalSelected > 0 && (
                    <button className="pfc-cfg-reset-btn" onClick={reset}>↺ Reset</button>
                )}
            </div>

            <div className="pfc-cfg-filters">
                {steps.map(step => {
                    const selected = selections[step.key] || []
                    const isOpen = openStep === step.key
                    const availableOptions = getAvailableOptions(step)

                    let triggerText = 'Any'
                    if (step.isNumeric) {
                        const sel = selections[step.key]
                        const currentMin = (sel && typeof sel === 'object' && sel.min !== undefined) ? sel.min : step.min
                        const currentMax = (sel && typeof sel === 'object' && sel.max !== undefined) ? sel.max : step.max
                        if (currentMin !== step.min || currentMax !== step.max) {
                            triggerText = `${currentMin} - ${currentMax}`
                        }
                    } else {
                        if (selected.length === 1) triggerText = selected[0].length > 18 ? selected[0].substring(0, 16) + '…' : selected[0]
                        else if (selected.length > 1) triggerText = `${selected.length} selected`
                    }

                    const hasValue = step.isNumeric
                        ? (selections[step.key] && typeof selections[step.key] === 'object' && (selections[step.key].min !== step.min || selections[step.key].max !== step.max))
                        : selected.length > 0

                    return (
                        <div className="pfc-cfg-filter" key={step.key}>
                            <div className="pfc-filter-label">{step.label}</div>
                            <button
                                className={`pfc-filter-trigger${isOpen ? ' open' : ''}${hasValue ? ' has-value' : ''}`}
                                onClick={() => setOpenStep(isOpen ? null : step.key)}
                            >
                                <span className="pfc-filter-trigger-text">{triggerText}</span>
                                <svg className="pfc-filter-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                                    <path d={isOpen ? 'M1 5L5 1L9 5' : 'M1 1L5 5L9 1'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            {isOpen && (
                                <div className="pfc-filter-panel" style={step.isNumeric ? { overflow: 'visible', maxHeight: 'none' } : undefined}>
                                    {step.isNumeric ? (() => {
                                        const sel = selections[step.key]
                                        const currentMin = (sel && typeof sel === 'object' && sel.min !== undefined) ? sel.min : step.min
                                        const currentMax = (sel && typeof sel === 'object' && sel.max !== undefined) ? sel.max : step.max
                                        const percentMin = ((currentMin - step.min) / (step.max - step.min)) * 100
                                        const percentMax = ((currentMax - step.min) / (step.max - step.min)) * 100

                                        return (
                                            <div style={{ padding: '12px 16px', minWidth: '240px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px', fontWeight: 600, color: 'var(--pfc-text2)' }}>
                                                    <span>Min: {currentMin}</span>
                                                    <span>Max: {currentMax}</span>
                                                </div>
                                                <div className="pfc-dual-range">
                                                    <div className="pfc-slider-track" />
                                                    <div 
                                                        className="pfc-slider-highlight" 
                                                        style={{
                                                            left: `${percentMin}%`,
                                                            width: `${percentMax - percentMin}%`
                                                        }}
                                                    />
                                                    <input 
                                                        type="range" 
                                                        min={step.min} 
                                                        max={step.max} 
                                                        step={step.stepSize} 
                                                        value={currentMin} 
                                                        onChange={(e) => {
                                                            const val = parseFloat(e.target.value)
                                                            const nextMin = Math.min(val, currentMax)
                                                            setNumericRange(step.key, nextMin, currentMax)
                                                        }} 
                                                    />
                                                    <input 
                                                        type="range" 
                                                        min={step.min} 
                                                        max={step.max} 
                                                        step={step.stepSize} 
                                                        value={currentMax} 
                                                        onChange={(e) => {
                                                            const val = parseFloat(e.target.value)
                                                            const nextMax = Math.max(val, currentMin)
                                                            setNumericRange(step.key, currentMin, nextMax)
                                                        }} 
                                                    />
                                                </div>
                                                <div style={{ fontSize: '10px', color: 'var(--pfc-text3)', marginTop: '8px', textAlign: 'center', fontFamily: 'var(--pfc-mono)' }}>
                                                    Range: {step.min} to {step.max}
                                                </div>
                                            </div>
                                        )
                                    })() : (
                                        availableOptions.length === 0 ? (
                                            <div className="pfc-filter-empty">No options available</div>
                                        ) : (
                                            availableOptions.map(opt => (
                                                <label className="pfc-filter-opt" key={opt}>
                                                    <input
                                                        type="checkbox"
                                                        className="pfc-filter-checkbox"
                                                        checked={selected.includes(opt)}
                                                        onChange={() => toggleOption(step.key, opt)}
                                                    />
                                                    <span className="pfc-filter-opt-text">{opt}</span>
                                                </label>
                                            ))
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {filledCount > 0 && (
                <div className="pfc-cfg-match">
                    <span className="pfc-cfg-match-count">{matchedProducts.length}</span>
                    <span className="pfc-cfg-match-label"> product{matchedProducts.length !== 1 ? 's' : ''} match</span>
                </div>
            )}

            <div className="pfc-cfg-contact">
                <div className="pfc-cfg-contact-text">Questions about your application? Our engineers are happy to help.</div>
                <div className="pfc-cfg-contact-phone">(916) 626-3029</div>
            </div>
        </div>
    )
}

export default ProductConfigurator
